import { css, cx } from '@emotion/css';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import SVG from 'react-inlinesvg';

import { type GrafanaTheme2 } from '@grafana/data';
import { selectors } from '@grafana/e2e-selectors';
import { t } from '@grafana/i18n';
import { config } from '@grafana/runtime';
import { type SceneComponentProps, sceneGraph, SceneObjectBase } from '@grafana/scenes';
import { ScrollContainer, Sidebar, useStyles2 } from '@grafana/ui';
import { getLayoutType } from 'app/features/dashboard/utils/tracking';
import addPanelSvg from 'img/dashboards/add-panel.svg';

import { useClipboardState } from '../../scene/layouts-shared/useClipboardState';
import { getDashboardSceneLike } from '../../scene/types/dashboard';
import { DashboardInteractions } from '../../utils/interactions';
import { DashboardSidebar } from '../DashboardSidebar';

import { AddAnnotationQuery } from './AddAnnotationQuery';
import { AddButton } from './AddButton';
import { AddFilters } from './AddFilters';
import { AddLink } from './AddLink';
import { AddNewSection } from './AddNewSection';
import { AddRow } from './AddRow';
import { AddTab } from './AddTab';
import { AddVariable } from './AddVariable';

export class AddNewPane extends SceneObjectBase {
  public static Component = AddNewPaneRenderer;
  public getId() {
    return 'add' as const;
  }
}

function AddNewPaneRenderer({ model }: SceneComponentProps<AddNewPane>) {
  const sidebar = sceneGraph.getAncestor(model, DashboardSidebar);
  const { hasCopiedPanel } = useClipboardState();
  const styles = useStyles2(getStyles);
  const dashboardScene = getDashboardSceneLike(model);
  const orchestrator = dashboardScene.state.layoutOrchestrator;
  const selectedObj = sidebar.getSelectedObject();

  const onStartDragging = (result: { draggableId: string }) => {
    const mode = result.draggableId === 'paste-panel-drag' ? 'paste' : 'newPanel';
    orchestrator.startDraggingNewPanel(mode);
  };

  const pastePanel = () => {
    sidebar.pastePanel(selectedObj);
    DashboardInteractions.trackPastePanelClick('sidebar', getLayoutType(selectedObj), 'click');
  };

  return (
    <div className={styles.wrapper}>
      <Sidebar.PaneHeader title={t('dashboard.sidebar.add.pane-header', 'Add')} />
      <ScrollContainer showScrollIndicators={true}>
        <AddNewSection
          title={t('dashboard.sidebar.add.new-panel.title', 'Panel')}
          description={t('dashboard.sidebar.add.new-panel.description', 'Drag or click to add a panel')}
        >
          <DragDropContext onDragStart={onStartDragging} onDragEnd={() => {}}>
            <Droppable droppableId="side-drop-id" isDropDisabled>
              {(dropProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  <Draggable draggableId="new-panel-drag" index={0}>
                    {(dragProvided, dragSnapshot) => {
                      return (
                        <div
                          role="button"
                          data-testid={selectors.components.Sidebar.newPanelButton}
                          tabIndex={0}
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={cx(styles.imageContainer, dragSnapshot.isDragging && styles.dragging)}
                          onClick={() => sidebar.addNewPanel(selectedObj)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              sidebar.addNewPanel(selectedObj);
                            }
                          }}
                          aria-label={t('dashboard.sidebar.add.new-panel.title', 'Panel')}
                        >
                          {/* @ts-expect-error react-inlinesvg@4.3.0 return type includes bigint, which isn't in @types/react@18's ReactNode. Remove when we update @types/react. */}
                          <SVG
                            title={t('dashboard.sidebar.add.new-panel.button', 'Add new panel button')}
                            src={addPanelSvg}
                          />
                        </div>
                      );
                    }}
                  </Draggable>
                  {hasCopiedPanel && (
                    <Draggable
                      draggableId="paste-panel-drag"
                      index={1}
                      isDragDisabled={!hasCopiedPanel}
                      disableInteractiveElementBlocking={true}
                    >
                      {(dragProvided, _) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                        >
                          <AddButton
                            className={styles.pasteButton}
                            icon="clipboard-alt"
                            tabIndex={0}
                            onClick={() => pastePanel()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                pastePanel();
                              }
                            }}
                            aria-label={t('dashboard.sidebar.add.paste-panel.title', 'Paste panel')}
                            label={t('dashboard.sidebar.add.paste-panel.title', 'Paste panel')}
                            tooltip={t('dashboard.sidebar.add.paste-panel.description', 'Click or drag to paste panel')}
                          ></AddButton>
                        </div>
                      )}
                    </Draggable>
                  )}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </AddNewSection>
        <AddNewSection title={t('dashboard.sidebar.add.group-layouts', 'Group layouts')}>
          <AddRow dashboardScene={dashboardScene} selectedElement={selectedObj} />
          <AddTab dashboardScene={dashboardScene} selectedElement={selectedObj} />
        </AddNewSection>
        <AddNewSection title={t('dashboard.sidebar.add.dashboard-controls', 'Dashboard controls')}>
          {config.featureToggles.dashboardUnifiedDrilldownControls && <AddFilters dashboardScene={dashboardScene} />}
          <AddVariable dashboardScene={dashboardScene} selectedElement={selectedObj} />
          <AddAnnotationQuery dashboardScene={dashboardScene} />
          <AddLink dashboardScene={dashboardScene} />
        </AddNewSection>
      </ScrollContainer>
    </div>
  );
}

function getStyles(theme: GrafanaTheme2) {
  return {
    wrapper: css({
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      height: '100%',
    }),
    dragging: css({
      cursor: 'move',
    }),
    imageContainer: css({
      cursor: 'pointer',
      width: '100%',
      opacity: 0.8,
      overflow: 'hidden',
      borderRadius: theme.shape.radius.default,
      border: `1px solid ${theme.colors.border.medium}`,
      background: theme.colors.background.secondary,
      '&:hover': {
        opacity: 1,
      },
      svg: {
        color: theme.colors.accent.main,
        display: 'block',
        width: 'auto',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: theme.spacing(9),
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    }),
    pasteButton: css({
      width: '100%',
      marginTop: theme.spacing(2),
    }),
  };
}
